<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
    @click.self="closeModal"
  >
    <div class="w-full max-w-md p-6 bg-white rounded-xl shadow-2xl">
      <!-- Icon -->
      <div class="flex justify-center mb-4">
        <div
          class="w-16 h-16 rounded-full flex items-center justify-center"
          :class="iconClasses"
        >
          <svg
            class="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              v-if="type === 'success'"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
            <path
              v-else-if="type === 'error'"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
            <path
              v-else-if="type === 'warning'"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
            <path
              v-else
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
      </div>

      <!-- Title -->
      <h3 class="text-xl font-bold text-center text-gray-900 mb-2">
        {{ title }}
      </h3>

      <!-- Message -->
      <p class="text-gray-600 text-center mb-6">
        {{ message }}
      </p>

      <!-- Actions -->
      <div class="flex gap-3">
        <button
          v-if="showCancel"
          class="flex-1 px-4 py-2 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50"
          @click="handleCancel"
        >
          {{ cancelText }}
        </button>
        <button
          class="px-4 py-2 text-white transition-colors rounded-lg"
          :class="confirmButtonClasses"
          :style="{ minWidth: showCancel ? 'auto' : '100%' }"
          @click="handleConfirm"
        >
          {{ confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// Props
interface Props {
	isOpen: boolean
	type?: 'success' | 'error' | 'warning' | 'info'
	title: string
	message: string
	confirmText?: string
	cancelText?: string
	showCancel?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	type: 'info',
	confirmText: 'OK',
	cancelText: 'Abbrechen',
	showCancel: false
})

// Emits
const emit = defineEmits<{
	close: []
	confirm: []
	cancel: []
}>()

// Computed
const iconClasses = computed(() => {
	switch (props.type) {
		case 'success':
			return 'bg-green-100 text-green-600'
		case 'error':
			return 'bg-red-100 text-red-600'
		case 'warning':
			return 'bg-yellow-100 text-yellow-600'
		default:
			return 'bg-blue-100 text-blue-600'
	}
})

const confirmButtonClasses = computed(() => {
	switch (props.type) {
		case 'success':
			return 'bg-green-600 hover:bg-green-700'
		case 'error':
			return 'bg-red-600 hover:bg-red-700'
		case 'warning':
			return 'bg-yellow-600 hover:bg-yellow-700'
		default:
			return 'bg-blue-600 hover:bg-blue-700'
	}
})

// Methods
const closeModal = () => {
	emit('close')
}

const handleConfirm = () => {
	emit('confirm')
	closeModal()
}

const handleCancel = () => {
	emit('cancel')
	closeModal()
}
</script>
